import type { UserRole } from '@okra/shared'

type RoleName = 'STOCK_MGR' | 'ACCOUNTANT' | 'SALES_REP' | 'AGENCY_MGR' | 'DIRECTOR' | 'ADMIN'

/**
 * Minimum role required to access each nav section.
 * Roles are hierarchical: ADMIN > DIRECTOR > AGENCY_MGR > SALES_REP
 * STOCK_MGR and ACCOUNTANT are transverse roles outside the hierarchy and
 * are granted access through routeExplicitRoles below.
 */
export const routePermissions: Record<string, RoleName> = {
  '/dashboard': 'SALES_REP',
  '/sales': 'SALES_REP',
  '/clients': 'SALES_REP',
  '/products': 'DIRECTOR',
  '/pricing': 'DIRECTOR',
  '/suppliers': 'DIRECTOR',
  '/cash': 'SALES_REP',
  '/stock': 'DIRECTOR',
  '/purchases': 'DIRECTOR',
  '/agencies': 'DIRECTOR',
  '/users': 'AGENCY_MGR',
  '/periods': 'DIRECTOR',
  '/credit/overdue': 'SALES_REP',
  '/exports': 'DIRECTOR',
  '/accounts': 'DIRECTOR',
  '/stats': 'SALES_REP',
  '/settings': 'ADMIN',
  '/settings/client-categories': 'ADMIN',
}

export const roleHierarchy: Record<RoleName, number> = {
  STOCK_MGR: 0,
  ACCOUNTANT: 0,
  SALES_REP: 1,
  AGENCY_MGR: 2,
  DIRECTOR: 3,
  ADMIN: 4,
}

// Routes accessibles à des rôles spécifiques indépendamment de la hiérarchie
export const routeExplicitRoles: Partial<Record<string, RoleName[]>> = {
  '/stock': ['STOCK_MGR', 'ACCOUNTANT'],
  '/purchases': ['ACCOUNTANT'],
  '/suppliers': ['ACCOUNTANT'],
  '/periods': ['ACCOUNTANT'],
  '/credit/overdue': ['ACCOUNTANT'],
  '/exports': ['ACCOUNTANT'],
  '/accounts': ['ACCOUNTANT'],
  '/stats': ['ACCOUNTANT'],
}

export function canAccess(userRole: UserRole | RoleName, requiredRole: UserRole | RoleName, route?: string): boolean {
  if (route && routeExplicitRoles[route]?.includes(userRole as RoleName)) return true
  // Transverse roles must NOT pass hierarchical checks for unrelated routes
  if (userRole === 'STOCK_MGR' || userRole === 'ACCOUNTANT') return false
  return roleHierarchy[userRole as RoleName] >= roleHierarchy[requiredRole as RoleName]
}
